/**
 * KYC Version Selector Component
 * TASK-202: Version history dropdown for KYC profiles
 *
 * Displays a dropdown of all KYC profile versions, allowing users to:
 * - View the current version (default)
 * - Select and view historical versions
 * - See when each version was created and by whom
 * - Identify the reason for changes
 */

import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, User } from 'lucide-react';
import kycService from '../../../../services/kycService';
import { toast } from 'sonner';
import type { KYCProfileVersion } from '../../../../types/kyc.types';

interface KYCVersionSelectorProps {
  clientId: string;
  currentVersion: KYCProfileVersion | null;
  onVersionSelect: (version: KYCProfileVersion) => void;
}

const KYCVersionSelector: React.FC<KYCVersionSelectorProps> = ({
  clientId,
  currentVersion,
  onVersionSelect
}) => {
  const [versions, setVersions] = useState<KYCProfileVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchVersions();
  }, [clientId]);

  const fetchVersions = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await kycService.getVersionHistory(clientId, 50, 0);
      setVersions(response.versions);

      // Auto-select current version if none selected
      if (!currentVersion && response.versions.length > 0) {
        const current = response.versions.find(v => v.isCurrent);
        if (current) {
          onVersionSelect(current);
        }
      }
    } catch (err: any) {
      console.error('Error fetching version history:', err);
      setError(err.message || 'Failed to load version history');
      toast.error('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangeReasonLabel = (reason?: string): string => {
    const labels: Record<string, string> = {
      'manual_update': 'Manual Update',
      'document_upload': 'Document Upload',
      'periodic_review': 'Periodic Review',
      'correction': 'Correction',
      'system_merge': 'System Merge'
    };
    return reason ? labels[reason] || reason : 'Unknown';
  };

  if (loading) {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Version History
        </label>
        <div className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
          <span className="text-gray-500">Loading versions...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Version History
        </label>
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Version History
        </label>
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
          <p className="text-sm text-gray-600">No version history available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        <Clock className="inline w-4 h-4 mr-1" />
        Version History ({versions.length} version{versions.length !== 1 ? 's' : ''})
      </label>

      <select
        value={currentVersion?.id || ''}
        onChange={(e) => {
          const selected = versions.find(v => v.id === e.target.value);
          if (selected) {
            onVersionSelect(selected);
          }
        }}
        className="w-full max-w-2xl px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        {versions.map((version) => {
          const changeCount = version.changedFieldsCount || 0;
          const changeSummary = version.changeSummary || getChangeReasonLabel(version.changeReason);

          return (
            <option key={version.id} value={version.id}>
              v{version.versionNumber}
              {version.isCurrent && ' (Current)'}
              {' - '}
              {formatDate(version.createdAt)}
              {' - '}
              {changeSummary}
              {changeCount > 0 && ` (${changeCount} field${changeCount !== 1 ? 's' : ''})`}
            </option>
          );
        })}
      </select>

      {/* Historical Version Warning */}
      {currentVersion && !currentVersion.isCurrent && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">
                Viewing Historical Version {currentVersion.versionNumber}
              </p>
              <p className="text-xs text-amber-800 mt-1">
                This is a read-only snapshot from {formatDate(currentVersion.createdAt)}.
                The data shown may differ from the current version.
              </p>
              {currentVersion.createdBy && (
                <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                  <User className="w-3 h-3" />
                  Modified by {currentVersion.createdBy}
                  {currentVersion.createdByRole && ` (${currentVersion.createdByRole})`}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Current Version Info */}
      {currentVersion && currentVersion.isCurrent && (
        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
          <p className="text-xs text-green-800">
            You are viewing the current version (v{currentVersion.versionNumber}).
            Last updated {formatDate(currentVersion.createdAt)}.
          </p>
        </div>
      )}

      {/* Version Statistics */}
      {currentVersion && currentVersion.changeCategories && (
        <div className="mt-2 flex gap-3 text-xs text-gray-600">
          {currentVersion.changeCategories.added > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              {currentVersion.changeCategories.added} added
            </span>
          )}
          {currentVersion.changeCategories.modified > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              {currentVersion.changeCategories.modified} modified
            </span>
          )}
          {currentVersion.changeCategories.removed > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              {currentVersion.changeCategories.removed} removed
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default KYCVersionSelector;
